import {useEffect, useState, useMemo, useCallback, useRef} from 'react'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {TimeSlot, Status} from '@wix/ambassador-table-reservations-v1-time-slot/types'

import {useExperienceDetailsStorage} from '../storage'
import {useGetLayoutSize} from '../../../components-shared/LayoutSizeProvider'
import {getRange} from '../../../utils/partySize'
import {RequestStatus} from '../../../utils/wrapRequest'
import {
  getEnabledReservationLocations,
  getReservationLocationById,
} from '../../../utils/reservationLocation'
import {getTimeSlotsPageType} from '../../../utils/getTimeSlotsPageType'
import {
  filterDateForExperience,
  getNearestWorkingDateForExperience,
} from '../../../utils/experienceSchedule'
import {reserveButtonDataHooks} from '../components/ReserveButton/constants'
import {useElementWidth} from '../../../utils/useElementWidth'

const DEFAULT_MIN_PARTY_SIZE = 1

export const useHooks = () => {
  const {t} = useTranslation()
  const {layoutSize} = useGetLayoutSize()
  const {
    breadcrumbsConfig,
    experience,
    dayTimeSlots,
    timeSlots,
    getDayTimeSlots,
    getTimeSlots,
    getTimeSlotsStatus,
    regionalSettings,
    getExperienceStatus,
    getDayTimeSlotsStatus,
    reservationLocations,
    handleReservationData,
    handleReservationDataStatus,
    isSiteBusinessPremium,
  } = useExperienceDetailsStorage()

  const {width: submitButtonContentWidth, readAndSetWidth: readAndSetSubmitButtonContentWidth} =
    useElementWidth<HTMLSpanElement>(reserveButtonDataHooks.button())

  const enabledReservationLocations = getEnabledReservationLocations(reservationLocations)

  const nearestWorkingDate = useMemo(() => {
    if (experience?.configuration?.onlineReservations?.businessSchedule) {
      return getNearestWorkingDateForExperience(
        experience.configuration.onlineReservations.businessSchedule,
      )
    }
  }, [experience?.configuration?.onlineReservations?.businessSchedule])

  const isSelectedReservationLocationEnabled = useMemo(() => {
    return enabledReservationLocations.some(
      (location) => location.id === experience?.reservationLocationId,
    )
  }, [experience?.reservationLocationId, enabledReservationLocations])

  const showReservationForm = useMemo(() => {
    return !!nearestWorkingDate && isSelectedReservationLocationEnabled && isSiteBusinessPremium
  }, [nearestWorkingDate, isSelectedReservationLocationEnabled, isSiteBusinessPremium])

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(nearestWorkingDate)

  const [partySize, setPartySize] = useState(() => {
    const minPartySize =
      experience?.configuration?.onlineReservations?.partySize?.min || DEFAULT_MIN_PARTY_SIZE
    return minPartySize
  })

  const reservationSectionRef = useRef<HTMLDivElement>(null)

  const selectedReservationLocation = useMemo(() => {
    return getReservationLocationById(experience?.reservationLocationId!, reservationLocations)
  }, [experience?.reservationLocationId, reservationLocations])

  const businessSchedule = experience?.configuration?.onlineReservations?.businessSchedule

  const richContent = experience?.configuration?.displayInfo?.richContent

  const partySizeOptions = useMemo(() => {
    if (!experience?.configuration?.onlineReservations?.partySize) {
      return []
    }

    const minPartySize =
      experience.configuration.onlineReservations.partySize.min ?? DEFAULT_MIN_PARTY_SIZE
    const maxPartySize =
      experience.configuration.onlineReservations.partySize.max ?? DEFAULT_MIN_PARTY_SIZE

    const partySizeRange = getRange({
      start: minPartySize,
      end: maxPartySize,
    })

    return partySizeRange.map((size) => ({
      id: size.toString(),
      value: t('uou-reservations.shared.guests', {number: size}),
      isSelectable: true,
    })) as DropdownOptionProps[]
  }, [t, experience?.configuration?.onlineReservations?.partySize])

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Date>()
  const [showNoAvailableTimeSlotNotification, setShowNoAvailableTimeSlotNotification] =
    useState(false)
  const [showSelectTimeSlotError, setShowSelectTimeSlotError] = useState(false)

  const loadDayTimeSlots = useCallback(
    (date?: Date) => {
      if (!experience?.id || !experience?.reservationLocationId || !date) {
        return
      }

      getDayTimeSlots({
        reservationLocationId: experience.reservationLocationId,
        partySize,
        date,
        experienceId: experience.id,
        timeZone: selectedReservationLocation?.location?.timeZone,
      })
    },
    [
      experience?.id,
      experience?.reservationLocationId,
      partySize,
      getDayTimeSlots,
      selectedReservationLocation?.location?.timeZone,
    ],
  )

  const loadTimeSlots = useCallback(
    (date?: Date) => {
      if (!experience?.id || !experience?.reservationLocationId || !date) {
        return
      }

      getTimeSlots({
        reservationLocationId: experience.reservationLocationId,
        partySize,
        date,
        statuses: [Status.AVAILABLE],
        experienceId: experience.id,
        timeZone: selectedReservationLocation?.location?.timeZone,
      })
    },
    [
      experience?.id,
      experience?.reservationLocationId,
      partySize,
      getTimeSlots,
      selectedReservationLocation?.location?.timeZone,
    ],
  )

  const experiencePageType = useMemo(() => {
    return getTimeSlotsPageType(timeSlots, selectedDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlots])

  const scrollToReservationSection = useCallback(() => {
    reservationSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  const isInitialDataInProgress =
    getExperienceStatus !== RequestStatus.RESOLVED ||
    getDayTimeSlotsStatus !== RequestStatus.RESOLVED ||
    getTimeSlotsStatus !== RequestStatus.RESOLVED

  useEffect(() => {
    loadDayTimeSlots(selectedDate)
    loadTimeSlots(selectedDate)
  }, [loadDayTimeSlots, loadTimeSlots, selectedDate])

  useEffect(() => {
    if (getTimeSlotsStatus !== RequestStatus.RESOLVED) {
      return setShowNoAvailableTimeSlotNotification(false)
    }
    const hasAvailableSlots = dayTimeSlots && dayTimeSlots.length > 0
    setShowNoAvailableTimeSlotNotification(!hasAvailableSlots)
  }, [dayTimeSlots, timeSlots, getTimeSlotsStatus])

  useEffect(() => {
    setSelectedDate(nearestWorkingDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handlers
  const handlePartySizeChange = (option: DropdownOptionProps) => {
    setPartySize(Number(option.id))
    loadDayTimeSlots(selectedDate)
    loadTimeSlots(selectedDate)
  }

  const handleDateChange = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate)
      setSelectedTimeSlot(undefined)
      loadDayTimeSlots(newDate)
      loadTimeSlots(newDate)
    },
    [loadDayTimeSlots, loadTimeSlots],
  )

  const handleFilterDate = useCallback(
    (date: Date) => {
      return filterDateForExperience({
        date,
        experienceBusinessSchedule: businessSchedule,
      })
    },
    [businessSchedule],
  )

  const handleTimeSlotChange = useCallback((timeSlot: TimeSlot) => {
    setSelectedTimeSlot(timeSlot.startDate!)
    setShowSelectTimeSlotError(false)
  }, [])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      readAndSetSubmitButtonContentWidth()

      if (isInitialDataInProgress || handleReservationDataStatus === RequestStatus.LOADING) {
        return
      }

      if (!selectedTimeSlot) {
        setShowSelectTimeSlotError(true)
        return
      }

      if (!experience?.id || !experience?.reservationLocationId) {
        return
      }

      handleReservationData({
        details: {
          reservationLocationId: experience.reservationLocationId,
          partySize,
          startDate: selectedTimeSlot,
        },
        timeZone: selectedReservationLocation?.location?.timeZone!,
        experienceId: experience.id,
      })
    },
    [
      isInitialDataInProgress,
      handleReservationDataStatus,
      selectedTimeSlot,
      experience?.id,
      experience?.reservationLocationId,
      partySize,
      selectedReservationLocation?.location?.timeZone,
      handleReservationData,
    ],
  )

  return {
    t,
    submitButtonContentWidth,
    layoutSize,
    breadcrumbsConfig,
    experience,
    richContent,
    isInitialDataInProgress,
    showReservationForm,
    reservationLocation: selectedReservationLocation,
    // Reservation form data
    partySizeOptions,
    partySize,
    selectedDate,
    selectedTimeSlot,
    dayTimeSlots,
    timeSlotsByDate: timeSlots,
    experiencePageType,
    showNoAvailableTimeSlotNotification,
    showSelectTimeSlotError,
    regionalSettings,
    handleReservationDataStatus,
    // Handlers
    handlePartySizeChange,
    handleDateChange,
    handleFilterDate,
    handleTimeSlotChange,
    handleSubmit,
    // Scroll functionality
    reservationSectionRef,
    scrollToReservationSection,
  }
}
